import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useState
} from 'react';
import { useDropzone } from 'react-dropzone';
import useTranslations from '../../../hooks/useTranslations';
import { FetchCallbacksContext } from '../../../internal-components/FetchCallbacks';
import fetchAPI from '../../../utils/fetchAPI';
import { FormContext, useField } from '../FormContext';
import FileUploadFieldView from '../view/FileUploadFieldView';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const formatFileSize = s => {
  if (s < 1e3) {
    return s + '\xa0B';
  }

  if (s < 1e6) {
    return Math.round(s / 1024) + '\xa0KB';
  }

  return (s / 1024 / 1024).toFixed(1) + '\xa0MB';
};

const wrapFilename = name =>
  'at_' + Date.now() + '.' + encodeURIComponent(name);

const unwrapFilename = name => decodeURIComponent(name.split(/_at_\d+\./)[1]);

/**
 * Extracts "f07599163" from "omnitrack/rafi5/forms/:formId/submissions/:entityId/f07599163_at_1562759842826.file.txt"
 */
const getFieldNameFromFileKey = key =>
  key.split('/').slice(-1)[0].split('_')[0];

const applyProgress = (state, action) =>
  state.map(file => {
    if (file.id === action.id) {
      file.progress = action.progress;
    }

    return file;
  });

const applyComplete = (state, action) =>
  state.map(file => {
    if (file.id === action.id) {
      file.complete = true;
    }

    return file;
  });

const reducer = (state, action) => {
  const { type } = action;

  switch (type) {
    case 'reload':
      return [...action.files];

    case 'drop':
      return [...state, ...action.files];

    case 'delete':
      return [...state.filter(f => f.id !== action.id)];

    case 'progress':
      return [...applyProgress(state, action)];

    case 'complete':
      return [...applyComplete(state, action)];

    default:
      return state;
  }
};

const FileUploadFieldEngine = ({
  internalId,
  value,
  setValue,
  required,
  readOnly,
  ...fieldProps // for future development, know whe have this
}) => {
  const [attachments, dispatch] = useReducer(reducer, []);
  const [submissionId, setSubmissionId] = useState('');
  const { getSubmissionsId, apiEndpoints, formId } = useContext(FormContext);
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const t = useTranslations();

  const fetchFiles = async () => {
    const submissionId = await getSubmissionsId(false);
    if (!submissionId || !apiEndpoints.fileUpload) {
      return;
    }
    setSubmissionId(submissionId);
    fetchAPI(apiEndpoints.fileUpload(formId, submissionId, internalId)).then(
      r => {
        const files = Object.keys(r)
          .filter(k => getFieldNameFromFileKey(k) === internalId)
          .map(k => ({
            id: 'at_' + r[k].key.split(`/${internalId}_at_`)[1],
            complete: true,
            progress: 100,
            name: unwrapFilename(r[k].key),
            size: r[k].size,
            fieldId: internalId
          }));
        dispatch({ type: 'reload', files });
      }
    );
  };

  const mutationObserver = new MutationObserver((mutations, observer) => {
    try {
      const classesBeforeMutation = mutations[0].target.className;
      if (!classesBeforeMutation.includes('hiddenByLogic')) {
        fetchFiles();
      }
    } catch (e) {
      console.log('mutationObserverError', e);
    }
    observer.disconnect();
  });

  useEffect(() => {
    const div = document.querySelector(`[field-id=${internalId}]`);
    if (div.classList.contains('hiddenByLogic')) {
      mutationObserver.observe(div, {
        attributes: true,
        attributeFilter: ['class']
      });
      return;
    }
    fetchFiles();
  }, [formId, getSubmissionsId, internalId]);

  const onDrop = useCallback(
    files => {
      if (readOnly || !apiEndpoints.fileUpload) {
        return;
      }
      getSubmissionsId().then(sId => {
        if (!submissionId) {
          setSubmissionId(sId);
        }
        files.forEach(file => {
          // 2% forgiveness to avoid "sorry, 10.0 MB is bigger than 10.0 MB"
          if (file.size > MAX_FILE_SIZE * 1.02) {
            openSnackbar(
              'error',
              t('forms.file_upload.too_big', {
                size: formatFileSize(file.size),
                // chop off decimal for the max size
                max: formatFileSize(MAX_FILE_SIZE).replace(/\.0\s/, '\xa0')
              })
            );
          } else {
            const id = wrapFilename(file.name);
            file.id = id;
            file.fieldId = internalId;
            dispatch({ type: 'drop', files: [file] });
            const formData = new FormData();
            formData.append('file', file, id);

            fetchAPI(apiEndpoints.fileUpload(formId, sId, internalId), {
              method: 'PUT',
              body: formData,
              onUploadProgress: e => {
                const progress = (e.loaded / e.total) * 100;
                dispatch({
                  type: 'progress',
                  id,
                  progress
                });
              }
            }).then(() => {
              dispatch({ type: 'complete', id });
            });
          }
        });
      });
    },
    [
      internalId,
      readOnly,
      openSnackbar,
      t,
      getSubmissionsId,
      formId,
      apiEndpoints
    ]
  );
  const dropzone = useDropzone({ onDrop, noClick: true });

  const onFieldClick = useCallback(
    event => {
      const clickedOnLink = event.target.closest('.dropzone-button, a');
      if (!clickedOnLink) {
        // ignore the click
        event.preventDefault();
      }
    },
    [readOnly]
  );

  const deleteFile = fileId => {
    if (!apiEndpoints.fileUpload) {
      return;
    }

    const fileKey = `${internalId}_${fileId}`;
    const endPoint = `${apiEndpoints.fileUpload(
      formId,
      submissionId,
      internalId
    )}/${fileKey}`;

    fetchAPI(endPoint, {
      method: 'DELETE'
    }).then(() => {
      dispatch({ type: 'delete', id: fileId });
    });
  };

  const validator = useCallback(
    value => {
      let error = '';

      if (required && !attachments.length) {
        error = t('forms.fields.error.required');
      }

      return error;
    },
    [required, attachments]
  );

  useField(internalId, validator);

  const viewProps = {
    internalId,
    value,
    setValue,
    required,
    readOnly,
    dropzone,
    onFieldClick,
    attachments,
    submissionId,
    deleteFile,
    attachmentCount: attachments.length
  };

  return <FileUploadFieldView {...viewProps} />;
};

export default FileUploadFieldEngine;
